import * as PIXI from 'pixi.js';
import planck from 'planck-js';

// Vector class for use in the game.
class Vector {
    constructor(x, y) {
        this.x = 0;
        this.y = 0;
        if (typeof x !== 'undefined' && typeof y === 'undefined') {
            this.x = x;
            this.y = x;
        } else if (typeof x !== 'undefined' && typeof y !== 'undefined') {
            this.x = x;
            this.y = y;
        }
    }

    length() {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }

    // Multiply me (modifies this object).
    mulm(vectorB) {
        this.x *= vectorB.x;
        this.y *= vectorB.y;
    }

    scalem(num) {
        this.x *= num;
        this.y *= num;
    }

    // Divide me (modifies this object).
    divm(vectorB) {
        this.x /= vectorB.x;
        this.y /= vectorB.y;
    }

    // Add me (modifies this object).
    addm(vectorB) {
        this.x += vectorB.x;
        this.y += vectorB.y;
    }

    // Subtract me (modifies this object).
    subm(vectorB) {
        this.x -= vectorB.x;
        this.y -= vectorB.y;
    }

    // Multiply (returns new object).
    mul(vectorB) {
        const n = this.copy();
        n.x *= vectorB.x;
        n.y *= vectorB.y;
        return n;
    }

    // Divide (returns new object).
    div(vectorB) {
        const n = this.copy();
        n.x /= vectorB.x;
        n.y /= vectorB.y;
        return n;
    }

    // Add (returns new object).
    add(vectorB) {
        const n = this.copy();
        n.x += vectorB.x;
        n.y += vectorB.y;
        return n;
    }

    // Subtract (returns new object).
    sub(vectorB) {
        const n = this.copy();
        n.x -= vectorB.x;
        n.y -= vectorB.y;
        return n;
    }

    scale(num) {
        const n = this.copy();
        n.x *= num;
        n.y *= num;
        return n;
    }

    normalize() {
        this.divm(new Vector(this.length()));
    }

    normalized() {
        return this.div(new Vector(this.length()));
    }

    copy() {
        return new Vector(this.x, this.y);
    }

    makePlanckVec2() {
        return planck.Vec2(this.x, this.y);
    }

    makePIXIPoint() {
        return new PIXI.Point(this.x, this.y);
    }

    // Move this point to a certain position.
    // Returns true if it reached the goal.
    moveTo(goal, speed) {
        const newPoint = this.copy();
        newPoint.subm(goal);
        const distance = newPoint.length();
        if (distance <= speed) {
            this.x = goal.x;
            this.y = goal.y;
            return true;
        }
        newPoint.normalize();
        newPoint.mulm(new Vector(-speed));
        newPoint.addm(this);
        this.x = newPoint.x;
        this.y = newPoint.y;
        return false;
    }

    serialize() {
        return { x: this.x, y: this.y };
    }

    deserialize(data) {
        this.x = Number(data.x);
        this.y = Number(data.y);
    }

    static multiply(vectorA, vectorB) {
        return vectorA.mul(vectorB);
    }

    static divide(vectorA, vectorB) {
        return vectorA.div(vectorB);
    }

    static add(vectorA, vectorB) {
        return vectorA.add(vectorB);
    }

    static subtract(vectorA, vectorB) {
        return vectorA.sub(vectorB);
    }

    static dot(vectorA, vectorB) {
        return vectorA.x * vectorB.x + vectorA.y * vectorB.y;
    }

    static fromPlanckVec2(planckVec2) {
        return new Vector(planckVec2.x, planckVec2.y);
    }

    static fromPIXIPoint(pixiPoint) {
        return new Vector(pixiPoint.x, pixiPoint.y);
    }

    static rotateAround(point, center, angle) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        return new Vector(cos * (point.x - center.x) - sin * (point.y - center.y) + center.x, cos * (point.y - center.y) + sin * (point.x - center.x) + center.y);
    }

    static getAngle(point, center) {
        return Math.atan2(center.y - point.y, center.x - point.x);
    }
}

export default Vector;
